// ============================================================================
// CAMPO "NUMERAÇÃO DOS ANEXOS — COMEÇAR EM"
// ARQUIVO: src/features/files/NumeracaoAnexosField.jsx
//
// Existe em TODA peça do Caderno de Autos, de qualquer tipo (decisão do titular,
// 24/09/2026). Mesmo lugar, mesmo tamanho, mesmo comportamento em toda peça.
// Acessibilidade (titular: dislexia + autismo): rótulo sempre em texto, nunca só
// no hover; fonte >= 14px; contraste alto; nada cortado; estado travado sempre
// explica POR QUÊ; cor nunca é o único aviso.
//
// Só mexe no estado local (store). Nada grava sozinho: o Firestore e o Drive só
// mudam ao clicar em "Salvar & Organizar" (regra de 26/08/2026).
// ============================================================================

import React, { useEffect, useState } from "react";
import {
  validarInicio,
  larguraDaPeca,
  numeroDoAnexo,
  rotuloAnexo,
} from "../../utils/numeracaoAnexos";

const formatarData = (iso) => {
  const d = new Date(`${String(iso || "").slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso || "") : d.toLocaleDateString("pt-BR");
};

export default function NumeracaoAnexosField({
  valor,
  onChange,
  totalSlots,
  ultimaPosicaoPreenchida = -1,
  protocoladaEm = "",
  somenteLeitura = false,
  arquivosParaRenomear = 0,
  carregarSugestoes = null,
}) {
  const [rascunho, setRascunho] = useState(String(valor ?? 1));
  const [erro, setErro] = useState("");
  // Botão "Continuar de outra peça": lê o estado FRESCO das outras peças do mesmo caderno
  // na hora do clique e só oferece os números; quem preenche o campo é o clique numa opção.
  const [sugestoes, setSugestoes] = useState(null);
  const [carregandoSugestoes, setCarregandoSugestoes] = useState(false);
  const [erroSugestoes, setErroSugestoes] = useState("");

  const abrirSugestoes = async () => {
    if (typeof carregarSugestoes !== "function") return;
    setCarregandoSugestoes(true);
    setErroSugestoes("");
    try {
      setSugestoes(await carregarSugestoes());
    } catch (e) {
      setSugestoes(null);
      setErroSugestoes(String(e?.message || e || "Não foi possível ler as outras peças."));
    } finally {
      setCarregandoSugestoes(false);
    }
  };

  useEffect(() => {
    setRascunho(String(valor ?? 1));
    setErro("");
  }, [valor]);

  const protocolada = Boolean(String(protocoladaEm || "").trim());
  const travado = protocolada || somenteLeitura;
  const inicio = Number(valor) >= 1 ? Number(valor) : 1;
  const largura = larguraDaPeca(totalSlots, inicio);
  const primeiro = rotuloAnexo(numeroDoAnexo(0, inicio), largura);
  const ultimoPreenchido =
    ultimaPosicaoPreenchida >= 0
      ? rotuloAnexo(numeroDoAnexo(ultimaPosicaoPreenchida, inicio), largura)
      : null;

  const confirmar = () => {
    if (travado) return;
    const v = validarInicio(rascunho, totalSlots);
    if (!v.ok) {
      setErro(v.motivo);
      return;
    }
    setErro("");
    if (v.valor !== inicio) onChange(v.valor);
    else setRascunho(String(v.valor));
  };

  return (
    <div
      data-testid="numeracao-anexos-campo"
      className="mt-2 rounded-md border border-gray-300 bg-white px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="numeracao-anexos-input"
          className="text-sm font-bold text-[#20243B]"
        >
          Numeração dos anexos — começar em
        </label>
        {somenteLeitura ? (
          <span
            data-testid="numeracao-anexos-valor"
            className="text-sm font-extrabold text-[#20243B]"
          >
            {inicio}
          </span>
        ) : (
          <input
            id="numeracao-anexos-input"
            data-testid="numeracao-anexos-input"
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={rascunho}
            disabled={travado}
            onChange={(e) => setRascunho(e.target.value)}
            onBlur={confirmar}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirmar();
              }
            }}
            aria-describedby="numeracao-anexos-ajuda"
            className="w-24 rounded-md border border-gray-400 bg-white px-2 py-1 text-sm font-bold text-[#20243B] disabled:bg-gray-100 disabled:text-gray-600"
          />
        )}
      </div>

      <div
        id="numeracao-anexos-ajuda"
        data-testid="numeracao-anexos-previa"
        className="mt-1 text-sm text-slate-700"
      >
        Primeiro anexo: <b>{primeiro}</b>
        {ultimoPreenchido ? (
          <>
            {" "}
            · Último preenchido: <b>{ultimoPreenchido}</b>
          </>
        ) : (
          <> · Nenhum anexo vinculado ainda.</>
        )}
      </div>

      {protocolada ? (
        <div
          data-testid="numeracao-anexos-travado"
          className="mt-1 text-sm font-semibold text-[#20243B]"
        >
          Travado: peça protocolada em {formatarData(protocoladaEm)}. Os números já
          apresentados ao tribunal não mudam.
        </div>
      ) : null}

      {somenteLeitura && !protocolada ? (
        <div className="mt-1 text-sm font-semibold text-[#20243B]">
          Somente leitura: o Word só mostra o que o Portal define.
        </div>
      ) : null}

      {erro ? (
        <div
          data-testid="numeracao-anexos-erro"
          role="alert"
          className="mt-1 text-sm font-bold text-red-700"
        >
          Atenção: {erro}
        </div>
      ) : null}

      {!travado ? (
        <div className="mt-1 text-sm text-slate-700">
          Os nomes no Drive só mudam ao clicar em <b>Salvar &amp; Organizar</b>.
        </div>
      ) : null}

      {!travado && typeof carregarSugestoes === "function" ? (
        <div className="mt-2" data-testid="numeracao-anexos-continuar">
          <button
            type="button"
            onClick={abrirSugestoes}
            disabled={carregandoSugestoes}
            className="rounded-md border border-gray-400 bg-white px-3 py-1 text-sm font-bold text-[#20243B] hover:bg-gray-50 disabled:opacity-60"
          >
            {carregandoSugestoes ? "Lendo as outras peças…" : "Continuar de outra peça"}
          </button>
          {erroSugestoes ? (
            <div role="alert" className="mt-1 text-sm font-bold text-red-700">
              Atenção: {erroSugestoes}
            </div>
          ) : null}
          {Array.isArray(sugestoes) ? (
            sugestoes.length === 0 ? (
              <div className="mt-1 text-sm text-slate-700">
                Nenhuma outra peça deste caderno tem anexos vinculados.
              </div>
            ) : (
              <ul className="mt-1 space-y-1" data-testid="numeracao-anexos-sugestoes">
                {sugestoes.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => {
                        const v = validarInicio(s.proximo, totalSlots);
                        if (!v.ok) {
                          setErro(v.motivo);
                          return;
                        }
                        setErro("");
                        onChange(v.valor);
                        setSugestoes(null);
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-1 text-left text-sm text-[#20243B] hover:bg-gray-50"
                    >
                      Continuar de <b>{s.titulo}</b> ({s.preenchidos} anexos vinculados): começar em{" "}
                      <b>{s.proximo}</b>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      ) : null}

      {largura === 3 ? (
        <div
          data-testid="numeracao-anexos-largura3"
          className="mt-1 text-sm font-semibold text-[#20243B]"
        >
          Esta peça usa números de 3 dígitos (a lista chega ao ANEXO 100 ou mais).
          {arquivosParaRenomear > 0
            ? ` ${arquivosParaRenomear} arquivo(s) vinculado(s) ainda estão com 2 dígitos e serão renomeados ao clicar em Salvar & Organizar.`
            : ""}
        </div>
      ) : arquivosParaRenomear > 0 ? (
        <div
          data-testid="numeracao-anexos-largura2"
          className="mt-1 text-sm font-semibold text-[#20243B]"
        >
          Esta peça voltou a usar números de 2 dígitos. {arquivosParaRenomear}{" "}
          arquivo(s) vinculado(s) ainda estão com 3 dígitos e serão renomeados ao clicar
          em Salvar &amp; Organizar.
        </div>
      ) : null}
    </div>
  );
}
